import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Brain, TrendingUp, TrendingDown, Minus, AlertCircle, CheckCircle, Loader2, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface FeedbackInsight {
  id: string;
  sentiment: 'positive' | 'negative' | 'neutral' | 'mixed';
  sentiment_score: number;
  topics: string[];
  key_concerns: string[];
  key_positives: string[];
  suggested_actions: string[];
  summary: string;
  created_at: string;
}

interface AIFeedbackInsightsCardProps {
  businessId: string;
}

export const AIFeedbackInsightsCard: React.FC<AIFeedbackInsightsCardProps> = ({ businessId }) => {
  const [insights, setInsights] = useState<FeedbackInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7');

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const lookbackDate = new Date();
      lookbackDate.setDate(lookbackDate.getDate() - parseInt(timeRange));

      const { data, error } = await supabase
        .from('ai_feedback_insights')
        .select('*')
        .eq('business_id', businessId)
        .gte('created_at', lookbackDate.toISOString())
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setInsights(data || []);
    } catch (err) {
      console.error('Error fetching insights:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, [businessId, timeRange]);

  // Calculate summary stats
  const sentimentCounts = {
    positive: insights.filter(i => i.sentiment === 'positive').length,
    negative: insights.filter(i => i.sentiment === 'negative').length,
    neutral: insights.filter(i => i.sentiment === 'neutral').length,
    mixed: insights.filter(i => i.sentiment === 'mixed').length,
  };

  const avgSentimentScore = insights.length > 0
    ? insights.reduce((sum, i) => sum + (i.sentiment_score || 0), 0) / insights.length
    : 0;

  // Collect all concerns and positives
  const allConcerns = insights.flatMap(i => i.key_concerns || []);
  const allPositives = insights.flatMap(i => i.key_positives || []);
  const allActions = insights.flatMap(i => i.suggested_actions || []);

  // Get unique items with counts
  const concernCounts = allConcerns.reduce((acc, c) => {
    acc[c] = (acc[c] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const positiveCounts = allPositives.reduce((acc, p) => {
    acc[p] = (acc[p] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const topConcerns = Object.entries(concernCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const topPositives = Object.entries(positiveCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'negative': return <TrendingDown className="h-4 w-4 text-red-500" />;
      case 'mixed': return <Minus className="h-4 w-4 text-yellow-500" />;
      default: return <Minus className="h-4 w-4 text-gray-500" />;
    }
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'negative': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'mixed': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-primary" />
          <CardTitle className="text-sm font-medium">AI Feedback Insights</CardTitle>
        </div>
        <div className="flex items-center gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={fetchInsights} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : insights.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Brain className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No AI insights yet</p>
            <p className="text-sm">Insights are generated when customers submit feedback</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Sentiment Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">{sentimentCounts.positive}</div>
                <div className="text-xs text-green-700 dark:text-green-400">Positive</div>
              </div>
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                <div className="text-2xl font-bold text-red-600">{sentimentCounts.negative}</div>
                <div className="text-xs text-red-700 dark:text-red-400">Negative</div>
              </div>
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-center">
                <div className="text-2xl font-bold text-yellow-600">{sentimentCounts.mixed}</div>
                <div className="text-xs text-yellow-700 dark:text-yellow-400">Mixed</div>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-900/20 rounded-lg text-center">
                <div className="text-2xl font-bold text-gray-600">{sentimentCounts.neutral}</div>
                <div className="text-xs text-gray-700 dark:text-gray-400">Neutral</div>
              </div>
            </div>

            {/* Sentiment Score */}
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Overall Sentiment Score</span>
                <div className="flex items-center gap-2">
                  <span className={`text-2xl font-bold ${avgSentimentScore >= 0.3 ? 'text-green-600' : avgSentimentScore <= -0.3 ? 'text-red-600' : 'text-yellow-600'}`}>
                    {avgSentimentScore >= 0 ? '+' : ''}{(avgSentimentScore * 100).toFixed(0)}%
                  </span>
                  {avgSentimentScore >= 0.3 ? (
                    <TrendingUp className="h-5 w-5 text-green-500" />
                  ) : avgSentimentScore <= -0.3 ? (
                    <TrendingDown className="h-5 w-5 text-red-500" />
                  ) : (
                    <Minus className="h-5 w-5 text-yellow-500" />
                  )}
                </div>
              </div>
              <div className="mt-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${avgSentimentScore >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                  style={{ width: `${Math.abs(avgSentimentScore) * 50 + 50}%`, marginLeft: avgSentimentScore < 0 ? `${50 - Math.abs(avgSentimentScore) * 50}%` : '50%' }}
                />
              </div>
            </div>

            {/* Top Concerns */}
            {topConcerns.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  Top Concerns
                </h4>
                <div className="space-y-2">
                  {topConcerns.map(([concern, count], i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-red-50 dark:bg-red-900/20 rounded">
                      <span className="text-sm">{concern}</span>
                      <Badge className="text-xs">{count}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Positives */}
            {topPositives.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Top Positives
                </h4>
                <div className="space-y-2">
                  {topPositives.map(([positive, count], i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 rounded">
                      <span className="text-sm">{positive}</span>
                      <Badge className="text-xs">{count}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Insights */}
            <div>
              <h4 className="text-sm font-medium mb-2">Recent Feedback</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {insights.slice(0, 5).map((insight) => (
                  <div key={insight.id} className="p-3 border rounded-lg">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {getSentimentIcon(insight.sentiment)}
                        <Badge className={getSentimentColor(insight.sentiment)}>
                          {insight.sentiment}
                        </Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(insight.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm mt-2 text-muted-foreground">{insight.summary}</p>
                    {insight.topics.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {insight.topics.map((topic, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {topic}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
